import React, { useMemo } from 'react';
import type { ConfiguredService } from '../../../types/service.types';
import { getProductCurrency } from '@/features/products/services/productCurrency';
import { useQuote } from '@features/quote/context/QuoteContext';
import { useAvailableServices } from '../../../hooks';
import { useScheduleOverrides } from '../../../hooks/useScheduleOverrides';
import type { Vehicle } from '@features/vehiclesearch/types/vinvrmsearch.types';
import type { VehicleDerivative } from '@features/vehiclesearch/types/manualsearch.types';
import { todayDateKey } from '@features/quote/utils/dateKey';
import { vetOverrideDatesForRequest } from '../../../utils/scheduleOverridesGuard';
import { deriveVehicleScheduleContext } from '@/app/utils/vehicleScheduleContext';
import {
    resolveServiceCountMode,
    resolveEffectiveServiceCountBounds,
} from '../../../utils/resolveServiceCountMode';
import { resolveVehicleParams } from './derive';
import { useDerivedServices } from './useDerivedServices';
import { DerivedServicesPanel } from './DerivedServicesPanel';
import type { PricedBasket } from '@features/quote/types/basketPricing.types';

interface ServiceConfiguratorProps {
    setConfiguredServices: React.Dispatch<React.SetStateAction<ConfiguredService[]>>;
    /**
     * Optional vehicle override. When provided, this is used to derive
     * make/model/derivative IDs for the services lookup. If omitted, the
     * vehicle is read from `useQuote()` to preserve the QuoteWizard flow.
     */
    selectedVehicle?: Vehicle | VehicleDerivative | null;
    className?: string;
    pricing?: PricedBasket;
}

export const ServiceConfigurator: React.FC<ServiceConfiguratorProps> = ({
    setConfiguredServices,
    selectedVehicle: selectedVehicleProp,
    className,
    pricing,
}) => {
    const {
        selectedVehicle: selectedVehicleFromQuote,
        selectedProduct,
        setResolvedPlanId,
        configuredAddOns,
        scheduleOverrides,
        applyDerivedSchedule,
        clearPaymentSelection,
    } = useQuote();
    // Prefer the prop when supplied; the standalone service-plan flow does not
    // mirror its vehicle into the quote context.
    const selectedVehicle =
        selectedVehicleProp !== undefined ? selectedVehicleProp : selectedVehicleFromQuote;
    const currency = getProductCurrency(selectedProduct);

    // Mode resolution — pure function of the product's config_settings,
    // already on the frontend. Never calls the network.
    const mode = useMemo(
        () => resolveServiceCountMode(selectedProduct?.config_settings),
        [selectedProduct?.config_settings]
    );

    // Frontend read of the same catalog rows: guided mode requests count = max up front.
    // The endpoint's response `bounds` remains authoritative for enforcement.
    const effectiveBounds = useMemo(
        () => resolveEffectiveServiceCountBounds(selectedProduct?.config_settings),
        [selectedProduct?.config_settings]
    );
    const requestedCount = mode === 'guided' ? effectiveBounds?.max : undefined;

    const vehicleParams = useMemo(() => resolveVehicleParams(selectedVehicle), [selectedVehicle]);
    // Extract Product
    const productParams = useMemo(() => {
        if (!selectedProduct) {
            return {
                product_id: '',
            };
        }

        return {
            product_id: selectedProduct.id,
        };
    }, [selectedProduct]);
    const queryParams = useMemo(() => {
        return {
            ...vehicleParams,
            ...productParams,
        };
    }, [vehicleParams, productParams]);
    const { getServiceById, isLoading, error } = useAvailableServices(queryParams);

    // Known before the derive answers, unlike the resolved plan id, so a date
    // saved against a different product or vehicle never reaches the request.
    const derivationContext = useMemo(
        () => ({
            productId: selectedProduct?.id ?? null,
            vehicleContext: deriveVehicleScheduleContext(selectedVehicle),
        }),
        [selectedProduct?.id, selectedVehicle]
    );

    const vettedDateOverrides = vetOverrideDatesForRequest(
        scheduleOverrides,
        todayDateKey(),
        derivationContext
    );

    const {
        derivedSchedule,
        isDerivedLoading,
        isDerivedFetching,
        isDerivedPending,
        isDerivedError,
        derivedError,
        bounds,
        derivedRows,
        configuredDerivedRows,
        derivedPricingResolved,
        canAddGuided,
        canRemoveGuided,
        handleGuidedAdd,
        handleGuidedRemove,
        fingerprint,
        dateOverridesInRequest,
    } = useDerivedServices({
        mode,
        productId: selectedProduct?.id,
        requestedCount,
        selectedVehicle,
        isLoading,
        getServiceById,
        setConfiguredServices,
        setResolvedPlanId,
        configuredAddOns,
        startEntryIndex: scheduleOverrides?.startEntryIndex ?? null,
        dateOverrides: vettedDateOverrides,
        storedFingerprint: scheduleOverrides?.derivationFingerprint,
        applyDerivedSchedule,
        clearPaymentSelection,
    });

    // `isFetching`/`isPending` (not `isLoading`) so a future `keepPreviousData`
    // on the derive query cannot make settled mean "stale rows, but cached".
    const isDerivedSettled = !isDerivedFetching && !isDerivedPending && !isLoading;
    const { setStartEntryIndex, setRowDate, isMaterialising, materialiseError, clearMaterialiseError } =
        useScheduleOverrides({
            fingerprint,
            derivationContext,
            derivedRows,
            isDerivedSettled,
            derivedError,
            dateOverridesInRequest,
            requestedCount,
        });

    return (
        <DerivedServicesPanel
            className={className}
            mode={mode}
            isLoading={isLoading}
            isDerivedLoading={isDerivedLoading}
            isDerivedError={isDerivedError}
            error={error}
            derivedSchedule={derivedSchedule}
            bounds={bounds}
            derivedRows={derivedRows}
            configuredDerivedRows={configuredDerivedRows}
            derivedPricingResolved={derivedPricingResolved}
            canAddGuided={canAddGuided}
            canRemoveGuided={canRemoveGuided}
            handleGuidedAdd={handleGuidedAdd}
            handleGuidedRemove={handleGuidedRemove}
            currency={currency}
            pricing={pricing}
            onStartIndexChange={setStartEntryIndex}
            setRowDate={setRowDate}
            isMaterialising={isMaterialising}
            materialiseError={materialiseError}
            clearMaterialiseError={clearMaterialiseError}
        />
    );
};
